import React from 'react';
import { Button, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MealMenu from './MealMenu';
import MealItemsHeader from './MealItemsHeader';
import MealItemEditor from './MealItemEditor';
import styles from './MealPlanPopup.module.css';
import { MAX_ITEMS_PER_MEAL, MAX_MEALS, createItem } from '../../pages/appointment/components/mealPlan/mealPlanModel';

const ADD_FOOD_SX = {
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--color-main)',
  padding: '2px 6px',
  minWidth: 0,
  marginLeft: '-6px',
  '& .MuiButton-startIcon': { marginRight: '4px' },
  '& .MuiSvgIcon-root': { fontSize: 16 },
};

/**
 * Uma refeição no editor, em duas zonas: cabeçalho cinza (horário, nome e
 * um menu) e corpo branco (observação quando pedida, itens em tabela leve
 * e o botão de novo alimento).
 */
const MealEditor = ({ meal, index, total, onChange, onMoveUp, onMoveDown, onDuplicate, onRemove }) => {
  const update = (changes) => onChange({ ...meal, ...changes });
  const showNote = !!(meal.noteOpen || meal.note);

  function updateItem(key, nextItem) {
    update({ items: meal.items.map((item) => (item.key === key ? nextItem : item)) });
  }

  // Troca o item com o vizinho de cima (-1) ou de baixo (+1)
  function moveItem(index, direction) {
    const target = index + direction;
    if (target < 0 || target >= meal.items.length) return;
    const items = [...meal.items];
    [items[index], items[target]] = [items[target], items[index]];
    update({ items });
  }

  function removeItem(key) {
    update({ items: meal.items.filter((item) => item.key !== key) });
  }

  function addItem() {
    if (meal.items.length >= MAX_ITEMS_PER_MEAL) return;
    update({ items: [...meal.items, createItem()] });
  }

  return (
    <div className={styles.meal}>
      <div className={styles.mealHead}>
        <TextField
          variant="standard"
          type="time"
          value={meal.time}
          onChange={(e) => update({ time: e.target.value })}
          className={`${styles.quiet} ${styles.timeField}`}
          InputProps={{ disableUnderline: true }}
          inputProps={{ 'aria-label': 'Horário da refeição' }}
        />
        <TextField
          variant="standard"
          placeholder="Nome da refeição"
          value={meal.name}
          onChange={(e) => update({ name: e.target.value })}
          className={`${styles.quiet} ${styles.nameField}`}
          InputProps={{ disableUnderline: true }}
          inputProps={{ maxLength: 60, 'aria-label': `Nome da refeição ${index + 1}` }}
          fullWidth
        />
        <div className={styles.mealMenu}>
          <MealMenu
            canMoveUp={index > 0}
            canMoveDown={index < total - 1}
            canDuplicate={total < MAX_MEALS}
            canAddNote={!showNote}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDuplicate={onDuplicate}
            onAddNote={() => update({ noteOpen: true })}
            onRemove={onRemove}
          />
        </div>
      </div>

      <div className={styles.mealBody}>
        {showNote && (
          <TextField
            variant="standard"
            placeholder="Observação da refeição"
            value={meal.note}
            onChange={(e) => update({ note: e.target.value })}
            className={`${styles.quiet} ${styles.mealNoteField}`}
            InputProps={{ disableUnderline: true }}
            inputProps={{ maxLength: 200, 'aria-label': 'Observação da refeição' }}
            autoFocus={!!meal.noteOpen && !meal.note}
            fullWidth
          />
        )}

        {meal.items.length > 0 && (
          <div className={styles.items}>
            <MealItemsHeader />
            {meal.items.map((item, index) => (
              <MealItemEditor
                key={item.key}
                item={item}
                canMoveUp={index > 0}
                canMoveDown={index < meal.items.length - 1}
                onMoveUp={() => moveItem(index, -1)}
                onMoveDown={() => moveItem(index, 1)}
                onChange={(nextItem) => updateItem(item.key, nextItem)}
                onRemove={() => removeItem(item.key)}
              />
            ))}
          </div>
        )}

        <div className={styles.mealFoot}>
          <Button
            variant="text"
            size="small"
            startIcon={<AddIcon />}
            onClick={addItem}
            disabled={meal.items.length >= MAX_ITEMS_PER_MEAL}
            sx={ADD_FOOD_SX}
          >
            Adicionar alimento
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MealEditor;
